import { useEffect, useMemo, useState } from 'react';

import ListSubheader from '@material-ui/core/ListSubheader';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { ListItemText, MenuItem, Select, TextField} from '@material-ui/core';

import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import DnsIcon from '@material-ui/icons/Dns';
import LockIcon from '@material-ui/icons/Lock';
import ExposureIcon from '@material-ui/icons/Exposure';
import ToysIcon from '@material-ui/icons/Toys';
import WbIncandescentIcon from '@material-ui/icons/WbIncandescent';
import BorderTopIcon from '@material-ui/icons/BorderTop';
import ImportExportIcon from '@material-ui/icons/ImportExport';

import { Apartment, DomoticsProvider, domoticsProviderOpts } from "../../../graphql/models";

import useApartmentElectronicsOptionsEditorStyles from './apartment_electronics_options_editor.style';

export interface ApartmentElectronicsOptionsEditorProps {
  apartment: Apartment;
  onChange: (updatedApartment: Apartment) => void;
}

export function ApartmentElectronicsOptionsEditor(
  props: ApartmentElectronicsOptionsEditorProps
) {
  const { apartment, onChange } = props;
  const classes = useApartmentElectronicsOptionsEditorStyles();

  const askWiserData = useMemo(() => {
    return apartment?.domoticsProvider === DomoticsProvider.SCHNEIDER_ELECTRIC;
  }, [apartment]);

  const [domoticsProvider, setDomoticsProvider] = useState<DomoticsProvider | undefined>(
    (apartment as Apartment)?.domoticsProvider ?? void 0
  );
  const [wiserUrl, setWiserUrl] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.wiserUrl ?? void 0
  );
  const [getGeneralAlarmAddress, setGetGeneralAlarmAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.getGeneralAlarmAddress ?? void 0
  );
  const [setGeneralAlarmAddress, setSetGeneralAlarmAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.setGeneralAlarmAddress ?? void 0
  );
  const [setGeneralFanAddress, setSetGeneralFanAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.setGeneralFanAddress ?? void 0
  );
  const [setGeneralLightsAddress, setSetGeneralLightsAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.setGeneralLightsAddress ?? void 0
  );
  const [setGeneralShuttersMoveAddress, setSetGeneralShuttersMoveAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.setGeneralShuttersMoveAddress ?? void 0
  );
  const [setGeneralShuttersStatusAddress, setSetGeneralShuttersStatusAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.setGeneralShuttersStatusAddress ?? void 0
  );
  const [getWinterSummerAddress, setGetWinterSummerAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.getWinterSummerAddress ?? void 0
  );
  const [setWinterSummerAddress, setSetWinterSummerAddress] = useState<string | undefined>(
    (apartment as Apartment)?.wiserOpts?.setWinterSummerAddress ?? void 0
  );

  const currentChangeValue = useMemo(() => ({
    ...apartment,
    domoticsProvider,
    wiserOpts: askWiserData ? {
      wiserUrl, getGeneralAlarmAddress, setGeneralAlarmAddress, 
      setGeneralFanAddress, setGeneralLightsAddress, setGeneralShuttersMoveAddress,
      setGeneralShuttersStatusAddress, getWinterSummerAddress, setWinterSummerAddress
    } : void 0
  }), [
    apartment, askWiserData, domoticsProvider, getGeneralAlarmAddress, 
    setGeneralAlarmAddress, setGeneralFanAddress, setGeneralLightsAddress, 
    setGeneralShuttersMoveAddress, setGeneralShuttersStatusAddress, 
    getWinterSummerAddress, setWinterSummerAddress, wiserUrl
  ]);

  const [fieldChanged, setFieldChanged] = useState(false);

  const handleFieldChange = (setter) => (value) => {
    setter(value);
    setFieldChanged(true);
  };

  useEffect(() => {
    if (fieldChanged) {
      onChange(currentChangeValue);
      setFieldChanged(false);
    }
  }, [fieldChanged, onChange, currentChangeValue]);
  
  return (
    <Paper elevation={3} style={{width: '100%', padding: '1rem'}}>
      <Typography variant='h4' color='textSecondary'>
        Informazioni domotica
      </Typography><br />
      <Grid container spacing={3}>
        <List
          component='nav'
          aria-labelledby={`apartment-${apartment?.id}-data-list-subheader`}
          subheader={
            <>
              <ListSubheader 
                component='div' 
                id={`apartment-${apartment?.id}-data-subheader`}
              >
                Proprietà dispositivo
              </ListSubheader>
            </>
          }
          style={{width: '100%'}}
        >
          <ListItem>
            <ListItemIcon>
              <AssignmentIndIcon />
            </ListItemIcon>
            <Select
              variant='outlined'
              displayEmpty
              className={classes.textField}
              value={domoticsProvider}
              onBlur={(evt) => setDomoticsProvider(evt.target.value as DomoticsProvider)}
            >
              <MenuItem value={undefined} disabled>
                Fornitore della domotica
              </MenuItem>
              {domoticsProviderOpts.map(opt => (
                <MenuItem key={opt} value={opt}>
                  <ListItemText primary={opt} />
                </MenuItem>
              ))}
            </Select>
          </ListItem>
          {askWiserData && (
            <>
              <ListSubheader 
                component='li' 
              >
                Dati per elettronica WISER
              </ListSubheader>
              <ListItem>
                <ListItemIcon>
                  <DnsIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={wiserUrl}
                  onBlur={(evt) => handleFieldChange(setWiserUrl)(evt.target.value)}
                  label='Indirizzo dispositivo (es. http://username:password@ip:port)'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LockIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={getGeneralAlarmAddress}
                  onBlur={(evt) => handleFieldChange(setGetGeneralAlarmAddress)(evt.target.value)}
                  label='Indirizzo GET stato antifurto'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LockIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={setGeneralAlarmAddress}
                  onBlur={(evt) => handleFieldChange(setSetGeneralAlarmAddress)(evt.target.value)}
                  label='Indirizzo SET stato antifurto'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ToysIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={setGeneralFanAddress}
                  onBlur={(evt) => handleFieldChange(setSetGeneralFanAddress)(evt.target.value)}
                  label='Indirizzo SET stato di tutti i climatizzatori'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WbIncandescentIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={setGeneralLightsAddress}
                  onBlur={(evt) => handleFieldChange(setSetGeneralLightsAddress)(evt.target.value)}
                  label='Indirizzo SET stato di tutte le luci'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BorderTopIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={setGeneralShuttersMoveAddress}
                  onBlur={(evt) => handleFieldChange(setSetGeneralShuttersMoveAddress)(evt.target.value)}
                  label='Indirizzo SET stato di tutte le tapparelle (accese/spente)'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ImportExportIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={setGeneralShuttersStatusAddress}
                  onBlur={(evt) => handleFieldChange(setSetGeneralShuttersStatusAddress)(evt.target.value)}
                  label='Indirizzo SET direzione di tutte le tapparelle (su/giù)'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ExposureIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={getWinterSummerAddress}
                  onBlur={(evt) => handleFieldChange(setGetWinterSummerAddress)(evt.target.value)}
                  label='Indirizzo GET per stabilire se il clima è in modalità estate o inverno'
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ExposureIcon />
                </ListItemIcon>
                <TextField 
                  className={classes.textField}
                  variant='outlined'
                  defaultValue={setWinterSummerAddress}
                  onBlur={(evt) => handleFieldChange(setSetWinterSummerAddress)(evt.target.value)}
                  label='Indirizzo SET per stabilire se il clima è in modalità estate o inverno'
                />
              </ListItem>
            </>
          )}
        </List>
      </Grid>
    </Paper>
  )
}